import React, { useEffect, useState } from "react";
import "./CorporateLeaderboardCard.css";
import { lifestyleApi } from "../apiService/api";
import { getAppParams, getUrlParams } from "../apiService/timespam";
import { useTranslation } from "react-i18next";

const BADGE_ICON = "https://appcdn.goqii.com/storeimg/76748_1780655929.png";
const STAR_ICON  = "https://appcdn.goqii.com/storeimg/90858_1780655970.png";

const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M3 9h18" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M8 2v4M16 2v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const pad = (n) => (n > 9 ? n : `0${n}`);

// Mirrors challenge-Revamp's Timer.js: counts down to challengeTimestamp,
// showing Days:Hours:Minutes, switching to Hours:Minutes:Seconds under 1 day left.
const useCountdown = (challengeTimestamp) => {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!challengeTimestamp) return undefined;

    const tick = () => {
      const distance = challengeTimestamp * 1000 - Date.now();
      if (distance < 0) {
        setRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setRemaining({
        days: Math.floor(distance / (24 * 60 * 60 * 1000)),
        hours: Math.floor((distance % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)),
        minutes: Math.floor((distance % (60 * 60 * 1000)) / (60 * 1000)),
        seconds: Math.floor((distance % (60 * 1000)) / 1000),
      });
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [challengeTimestamp]);

  return remaining;
};

const CorporateLeaderboardCard = () => {
  const { t } = useTranslation();
  const [card, setCard] = useState(null);
  const { theme } = getUrlParams();

  useEffect(() => {
    const appParams = getAppParams();
    if (!appParams.goqiiUserId) return;

    lifestyleApi
      .challengesCardData(appParams.goqiiUserId, "challengeRoutine", appParams)
      .then((res) => {
        const firstCard = res.data.data.corporateChallenges.data[0];
        setCard(firstCard);
      })
      .catch((e) => console.error("Error loading corporate challenge card", e));
  }, []);

  const {
    imageUrl,
    challengeTitle,
    challengeDateIst,
    userRank,
    userScore,
    keyword,
    updatedTime,
    arrowStatus,
    challengeState,
    challengeTimestamp,
    participantCount,
  } = card || {};

  const hasStarted = challengeState === "Started" || challengeState === "Ended";
  const countdown = useCountdown(!hasStarted ? challengeTimestamp : null);

  const arrowUp    = arrowStatus === "Up";
  const arrowDown  = arrowStatus === "Down";
  const arrowColor = arrowUp ? "#16a34a" : arrowDown ? "#dc2626" : "#9ca3af";
  const arrowSymbol = arrowUp ? "▲" : arrowDown ? "▼" : null;

  if (!card) return null;

  return (
    <div className={`clc-card${theme === "dark" ? " clc-dark" : ""}`}>

      {/* ── TOP: hexagon + title/date ── */}
      <div className="clc-top">

        {/* Hexagon logo */}
        <div className="clc-hex-wrap">
          {/* <svg className="clc-hex-svg" viewBox="0 0 100 114" fill="none">
            <path
              d="M45 4.04a10 10 0 0 1 10 0l33.48 19.33A10 10 0 0 1 93.48 32v50a10 10 0 0 1-5 8.66L55 109.96a10 10 0 0 1-10 0L11.52 90.63A10 10 0 0 1 6.52 82V32a10 10 0 0 1 5-8.66z"
              fill="#f8fffe"
              stroke="#a7f3d0"
              strokeWidth="2"
            />
          </svg> */}
          <img className="clc-hex-img" src={imageUrl} alt={challengeTitle} />
        </div>

        {/* Title + date */}
        <div className="clc-info">
          <div className="clc-title">{challengeTitle}</div>
          <div className="clc-date">
            <CalendarIcon />
            <span>{challengeDateIst}</span>
          </div>
          {hasStarted ? (
            /* ── STATS: full width ── */
            <div className="clc-stats">

              {/* RANK */}
              <div className="clc-stat">
                <div className=" clc-icon-rank">
                  <img src={BADGE_ICON} alt="rank" className="clc-icon-img" />
                </div>
                <div className="clc-stat-body">
                  <div className="clc-stat-value">
                    <span className="clc-num">{userRank}</span>
                    {arrowSymbol && (
                      <span className="clc-arrow" style={{ color: arrowColor }}>
                        {arrowSymbol}
                      </span>
                    )}
                  </div>
                  <div className="clc-stat-label">{t("leaderboard.rank", "RANK")}</div>
                </div>
              </div>

              <div className="clc-v-divider" />

              {/* POINTS */}
              <div className="clc-stat">
                <div className=" clc-icon-points">
                  <img src={STAR_ICON} alt="points" className="clc-icon-img" />
                </div>
                <div className="clc-stat-body">
                  <div className="clc-stat-value">
                    <span className="clc-num">{userScore}</span>
                  </div>
                  <div className="clc-stat-label">
                    {keyword || t("leaderboard.points", "POINTS")}
                  </div>
                </div>
              </div>

            </div>
          ) : (
            /* ── COUNTDOWN: challenge hasn't started yet ── */
            <div className="clc-countdown-row">
              <div className="clc-countdown">
                {countdown && countdown.days < 1 ? (
                  <>
                    <div className="clc-countdown-unit">
                      <span className="clc-countdown-num">{pad(countdown.hours)}</span>
                      <span className="clc-countdown-label">{t("leaderboard.hours", "HOURS")}</span>
                    </div>
                    <span className="clc-countdown-sep">:</span>
                    <div className="clc-countdown-unit">
                      <span className="clc-countdown-num">{pad(countdown.minutes)}</span>
                      <span className="clc-countdown-label">{t("leaderboard.minutes", "MINUTES")}</span>
                    </div>
                    <span className="clc-countdown-sep">:</span>
                    <div className="clc-countdown-unit">
                      <span className="clc-countdown-num">{pad(countdown.seconds)}</span>
                      <span className="clc-countdown-label">{t("leaderboard.seconds", "SECONDS")}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="clc-countdown-unit">
                      <span className="clc-countdown-num">{pad(countdown ? countdown.days : 0)}</span>
                      <span className="clc-countdown-label">{t("leaderboard.days", "DAYS")}</span>
                    </div>
                    <span className="clc-countdown-sep">:</span>
                    <div className="clc-countdown-unit">
                      <span className="clc-countdown-num">{pad(countdown ? countdown.hours : 0)}</span>
                      <span className="clc-countdown-label">{t("leaderboard.hours", "HOURS")}</span>
                    </div>
                    <span className="clc-countdown-sep">:</span>
                    <div className="clc-countdown-unit">
                      <span className="clc-countdown-num">{pad(countdown ? countdown.minutes : 0)}</span>
                      <span className="clc-countdown-label">{t("leaderboard.minutes", "MINUTES")}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="clc-participants">
                {participantCount > 0
                  ? t("leaderboard.youAndCount", "You & {{count}}", { count: participantCount })
                  : t("leaderboard.youOnly", "You")}{" "}
                <span className="clc-participants-sub">{t("leaderboard.participantsJoined", "Joined")}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── BOTTOM DIVIDER + updated at ── */}
      {hasStarted && (
        <>
          <div className="clc-h-divider" style={{ marginTop: 8 }} />
          <div className="clc-updated">
            <ClockIcon />
            <span>{t("leaderboard.updatedAt", "Updated at")} {updatedTime}</span>
          </div>
        </>
      )}

    </div>
  );
};

export default CorporateLeaderboardCard;
